/**
 * Header + footer for the public Voucher Redemption Locator, replicating ww2.uslawshield.com
 * (marketing 2026-10-14: "WW2's header" / "WW2's footer").
 *
 * STATIC REPLICA — last synced by hand from https://ww2.uslawshield.com/ on 2026-10-14. ww2 is a
 * WordPress (Avada) site; there is no embeddable header/footer feed, so links and legal copy are
 * copied here and must be re-synced when marketing/legal changes ww2. Everything links OUT to ww2.
 *
 * Deliberately NOT replicated (state- or session-specific on ww2; see the audit prompt):
 *  - the "Special Offer / FREESTART" promo bar and its footnote paragraph (only shown on ww2 once a
 *    state is selected, and the promo changes over time),
 *  - the "Selected State" picker (it sets ww2's own cookie; it has no meaning on our domain),
 *  - "Edit Cookie Consent Preferences" (ww2's cookie tool; this page sets no marketing cookies).
 *
 * Logo: our own copy (/USLSlogonobackground.png), not a hotlink to ww2's uploads. Fonts: see
 * VoucherLocator.css — Graphik/Gascogne are loaded from ww2.uslawshield.com (USLS enabled CORS for
 * the locator origins, 2026-10-16), with the previous fallbacks behind them.
 */
import React, { useState } from "react";

const WW2 = "https://ww2.uslawshield.com";
const PHONE_DISPLAY_TOP = "877.448.6839";
const PHONE_DISPLAY = "877-448-6839";
const PHONE_TEL = "tel:1-877-448-6839";

const NAV = [
  { label: "Self-Defense Plans", href: `${WW2}/carry-plans/` },
  { label: "EmergencyShield", href: `${WW2}/the-emergency-shield-plan/` },
  { label: "Member Stories", href: `${WW2}/member-stories/` },
];

const FOOTER_COLUMNS: {
  title: string;
  links: { label: string; href: string }[];
}[] = [
  {
    title: "Plans",
    links: [
      { label: "Carry Plans", href: `${WW2}/carry-plans/` },
      { label: "EmergencyShield™", href: `${WW2}/the-emergency-shield-plan/` },
    ],
  },
  {
    title: "Explore",
    links: [
      { label: "How It Works", href: `${WW2}/how-it-works/` },
      { label: "Stories", href: `${WW2}/member-stories/` },
      { label: "Meet the Attorneys", href: `${WW2}/meet-your-attorney/` },
      { label: "Events", href: `${WW2}/events/` },
    ],
  },
  {
    title: "Company",
    links: [
      { label: "About", href: `${WW2}/about-us/` },
      { label: "FAQ", href: `${WW2}/faq/` },
      { label: "Contact Us", href: `${WW2}/contact-us/` },
      {
        label: "Join Our Team",
        href: "https://www.uslawshield.com/employment/",
      },
    ],
  },
  {
    title: "Member",
    links: [
      { label: "Log In / Sign Up", href: `${WW2}/member-signup/` },
      { label: "Member Portal", href: "https://members.uslawshield.com/login" },
    ],
  },
];

const SOCIAL = [
  {
    label: "X (Twitter)",
    href: "https://twitter.com/uslawshield",
    d: "M17.75 3h3.07l-6.7 7.66L22 21h-6.17l-4.83-6.32L5.47 21H2.4l7.17-8.2L2 3h6.33l4.37 5.77L17.75 3zm-1.08 16.2h1.7L7.4 4.73H5.58L16.67 19.2z",
  },
  {
    label: "YouTube",
    href: "https://www.youtube.com/channel/UCzD8X6Dr-tBEy0Y_WiXlvmQ",
    d: "M22.54 6.42a2.78 2.78 0 00-1.94-2C18.88 4 12 4 12 4s-6.88 0-8.6.46a2.78 2.78 0 00-1.94 2A29 29 0 001 11.75a29 29 0 00.46 5.33A2.78 2.78 0 003.4 19.1c1.72.46 8.6.46 8.6.46s6.88 0 8.6-.46a2.78 2.78 0 001.94-2 29 29 0 00.46-5.25 29 29 0 00-.46-5.43zM9.75 15.02V8.48l5.75 3.27-5.75 3.27z",
  },
  {
    label: "Instagram",
    href: "https://www.instagram.com/uslawshield/",
    d: "M16 4H8a4 4 0 00-4 4v8a4 4 0 004 4h8a4 4 0 004-4V8a4 4 0 00-4-4zm2.5 12A2.5 2.5 0 0116 18.5H8A2.5 2.5 0 015.5 16V8A2.5 2.5 0 018 5.5h8a2.5 2.5 0 012.5 2.5v8zM12 8.5a3.5 3.5 0 100 7 3.5 3.5 0 000-7zm0 5.5a2 2 0 110-4 2 2 0 010 4zm4.5-6a1 1 0 11-2 0 1 1 0 012 0z",
  },
  {
    label: "Facebook",
    href: "https://www.facebook.com/USLawShield",
    d: "M18 2h-3a5 5 0 00-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 011-1h3z",
  },
  {
    label: "LinkedIn",
    href: "https://www.linkedin.com/company/us-law-shield/",
    d: "M16 8a6 6 0 016 6v7h-4v-7a2 2 0 00-4 0v7h-4v-7a6 6 0 016-6zM2 9h4v12H2zM4 2a2 2 0 110 4 2 2 0 010-4z",
  },
];

export const Ww2Header: React.FC = () => {
  const [open, setOpen] = useState(false);
  return (
    <header className="ww2-header" data-testid="ww2-header">
      <div className="ww2-topbar">
        <div className="ww2-wrap ww2-topbar-row">
          <div className="ww2-brand-toggle" role="group" aria-label="Brands">
            <a className="ww2-brand-toggle-on" href={`${WW2}/`}>
              U.S. LawShield
            </a>
            <a href="https://www.emergencyshield.com">EmergencyShield</a>
          </div>
          <div className="ww2-topbar-right">
            <a className="ww2-phone" href={PHONE_TEL}>
              {PHONE_DISPLAY_TOP}
            </a>
            <a
              className="ww2-login"
              href="https://members.uslawshield.com/login"
            >
              Log In
            </a>
          </div>
        </div>
      </div>
      <div className="ww2-navbar">
        <div className="ww2-wrap ww2-navbar-row">
          <a className="ww2-logo" href={`${WW2}/`}>
            <img src="/USLSlogonobackground.png" alt="U.S. LawShield" />
          </a>
          <nav className="ww2-nav" aria-label="Main">
            {NAV.map((l) => (
              <a key={l.label} href={l.href}>
                {l.label}
              </a>
            ))}
          </nav>
          <a className="ww2-cta" href={`${WW2}/member-signup/`}>
            Start Your Membership
          </a>
          <button
            type="button"
            className="ww2-burger"
            aria-label={open ? "Close menu" : "Open menu"}
            aria-expanded={open}
            aria-controls="ww2-mobile-nav"
            onClick={() => setOpen((o) => !o)}
          >
            <span />
            <span />
            <span />
          </button>
        </div>
        {open && (
          <nav
            id="ww2-mobile-nav"
            className="ww2-mobile-nav"
            aria-label="Main (mobile)"
          >
            {NAV.map((l) => (
              <a key={l.label} href={l.href}>
                {l.label}
              </a>
            ))}
            <a className="ww2-cta" href={`${WW2}/member-signup/`}>
              Start Your Membership
            </a>
          </nav>
        )}
      </div>
    </header>
  );
};

export const Ww2Footer: React.FC = () => {
  const year = new Date().getFullYear();
  return (
    <footer className="ww2-footer" data-testid="ww2-footer">
      <div className="ww2-wrap">
        <div className="ww2-footer-top">
          <div className="ww2-footer-brand">
            <img src="/USLSlogonobackground.png" alt="U.S. LawShield" />
            <p>We&rsquo;re by your side when it matters most.</p>
          </div>
          <div className="ww2-footer-cols">
            {FOOTER_COLUMNS.map((c) => (
              <div key={c.title}>
                <h4>{c.title}</h4>
                <ul>
                  {c.links.map((l) => (
                    <li key={l.label}>
                      <a href={l.href}>{l.label}</a>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
        <hr />
        <div className="ww2-legal">
          <p>
            EmergencyShield&trade; provides consultative coverage for legal
            emergency matters that occur after signup. EmergencyShield does not
            include coverage for preexisting legal matters. This limited
            engagement coverage is solely for matters and the time periods set
            forth in the terms of the EmergencyShield agreement. Coverage
            eligibility begins 30 days after purchase, unless expedited coverage
            eligibility fee is tendered during this period to expedite coverage
            eligibility to the date and time of such payment. For complete plan
            information, including details of legal services and benefits,
            coverage eligibility, pricing, and limitations and exclusions,
            please refer to the complete terms and conditions of the
            EmergencyShield agreement. Except as otherwise expressly provided in
            the individual membership agreements, no representations or
            warranties are made, whether express or implied. U.S. LawShield may
            periodically offer special or promotional offers, plans, or
            memberships where permissible. Promotional offers and eligibility
            are determined and offered by U.S. LawShield at its sole discretion,
            and U.S. LawShield reserves the right to extend, modify, or
            discontinue any such offer at any time without notice.
          </p>
          <p>
            U.S. LawShield&reg; and affiliated entities are not a law firm.
            Membership or participation in any U.S. LawShield program is not a
            guarantee of any particular outcome. U.S. LawShield and its
            affiliates offer prepaid legal service products. In some states,
            these products are offered by prepaid legal companies regulated
            under state law. In others, the applicable entity offers an
            insurance product. All legal services are provided by independent
            third-party program attorneys who are part of the program(s). Visit
            our{" "}
            <a href={`${WW2}/terms-and-conditions/`}>Terms and Conditions</a>{" "}
            and <a href={`${WW2}/privacy-policy/`}>Privacy Policy</a> for more
            information, including terms, conditions, restrictions, and
            insurance licensing information.
          </p>
        </div>
        <div className="ww2-social">
          {SOCIAL.map((s) => (
            <a
              key={s.label}
              href={s.href}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={s.label}
            >
              <svg
                viewBox="0 0 24 24"
                width="16"
                height="16"
                fill="currentColor"
                aria-hidden="true"
              >
                <path d={s.d} />
              </svg>
            </a>
          ))}
        </div>
        <div className="ww2-callus">
          <p className="ww2-callus-title">CALL US</p>
          <p>
            <a href={PHONE_TEL}>{PHONE_DISPLAY}</a>
          </p>
          <p>Mon-Fri: 8:00AM-8:00PM CST</p>
          <p>Sat-Sun: 8:30AM-5:00PM CST</p>
        </div>
        <div className="ww2-copyright">
          <span>
            &copy; Copyright {year} U.S. LawShield. All Rights Reserved.
          </span>
          <a href={`${WW2}/terms-and-conditions/`}>Terms and Conditions</a>
          <a href={`${WW2}/privacy-policy/`}>Privacy Policy</a>
        </div>
      </div>
    </footer>
  );
};
